// Canadian postal code → province inference.
//
// First letter of a Canadian postal code maps to a province/territory.
// Reference: Canada Post FSA list.
//
//   A → NL (Newfoundland and Labrador)
//   B → NS (Nova Scotia)
//   C → PE (Prince Edward Island)
//   E → NB (New Brunswick)
//   G → QC (Eastern QC)
//   H → QC (Montréal area)
//   J → QC (Western QC)
//   K → ON (Eastern ON)
//   L → ON (Central ON)
//   M → ON (Toronto)
//   N → ON (Southwestern ON)
//   P → ON (Northern ON)
//   R → MB (Manitoba)
//   S → SK (Saskatchewan)
//   T → AB (Alberta)
//   V → BC (British Columbia)
//   X → NT/NU (we default to NT; both share the X prefix and we ask user to confirm if needed)
//   Y → YT (Yukon)
//
// Letters D, F, I, O, Q, U, W, Z are NOT used in Canadian postal codes.

const FIRST_LETTER_TO_PROVINCE = {
  A: 'NL',
  B: 'NS',
  C: 'PE',
  E: 'NB',
  G: 'QC',
  H: 'QC',
  J: 'QC',
  K: 'ON',
  L: 'ON',
  M: 'ON',
  N: 'ON',
  P: 'ON',
  R: 'MB',
  S: 'SK',
  T: 'AB',
  V: 'BC',
  X: 'NT',
  Y: 'YT',
};

const PROVINCE_NAME_FR = {
  QC: 'Québec',
  ON: 'Ontario',
  AB: 'Alberta',
  BC: 'Colombie-Britannique',
  MB: 'Manitoba',
  NB: 'Nouveau-Brunswick',
  NL: 'Terre-Neuve-et-Labrador',
  NS: 'Nouvelle-Écosse',
  PE: 'Île-du-Prince-Édouard',
  SK: 'Saskatchewan',
  NT: 'Territoires du Nord-Ouest',
  NU: 'Nunavut',
  YT: 'Yukon',
};

const PROVINCE_NAME_EN = {
  QC: 'Quebec',
  ON: 'Ontario',
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  PE: 'Prince Edward Island',
  SK: 'Saskatchewan',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  YT: 'Yukon',
};

/**
 * Extract the first letter of the first complete postal-code-looking token
 * found in a Canadian address string and return the matching province code.
 * Returns null if no valid postal code FSA is detected.
 *
 * Examples (all return 'ON'):
 *   "123 Main St, Toronto, ON M5V 2H1"
 *   "M5V 2H1"
 *   "m5v2h1"
 */
export function inferProvinceFromAddress(address) {
  if (!address || typeof address !== 'string') return null;
  // Canadian postal code FSA: letter-digit-letter (e.g. M5V, H2X, V6B)
  // We accept the full 6-char postal code OR a standalone FSA.
  const match = address
    .toUpperCase()
    .match(/\b([A-Z]\d[A-Z])\s*\d?[A-Z]?\d?\b/);
  if (!match) return null;
  const firstLetter = match[1][0];
  return FIRST_LETTER_TO_PROVINCE[firstLetter] || null;
}

/**
 * Return the French/English display name for a province code.
 */
export function provinceName(code, lang = 'fr') {
  const c = (code || '').toUpperCase();
  return (lang === 'fr' ? PROVINCE_NAME_FR : PROVINCE_NAME_EN)[c] || c;
}
